import { ReactNode, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { getParams } from "../../utils/params";
import { cva, VariantProps } from "class-variance-authority";
import { twMerge } from "tailwind-merge";
const apiUrl = import.meta.env.VITE_API_URL;

export interface LoginProps extends VariantProps<typeof LoginStyles> {
    children?: ReactNode;
    className?: string;
}

export const LoginStyles = cva(
    "bg-[#fdf9e2] lg:w-[550px] text-left lg:mx-4 md:mx-8 lg:mx-auto mt-2 mb-6 lg:my-16 lg:my-8 px-4 md:px-8 py-8 lg:py-16 rounded-lg",
);

export const Login = ({ className }: LoginProps) => {
    const [email, setEmail] = useState("");
    const [error, setError] = useState(false);
    const [isMessageSent, setIsMessageSent] = useState<boolean>(false);
    const { t, i18n } = useTranslation();
    const emailInputRef = useRef<HTMLInputElement | null>(null);
    const authState = getParams("authState");
    const destination = getParams("destination");

    const mutation = useMutation<Response, Error, string>({
        mutationFn: async (emailToUse: string) => {
            const res = await fetch(`${apiUrl}/login`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    email: emailToUse.toLowerCase(),
                    lang: i18n.language,
                    ...(authState && { authState: authState }),
                    ...(destination && { destination: destination }),
                }),
            });
            return res;
        },
    });

    return (
        <div className={twMerge(LoginStyles({ className }))}>
            {!isMessageSent ? (
                <div>
                    <div>
                        <h1 className="mb-2 text-4xl font-bold">
                            {t("loginTitle")}
                        </h1>
                        <h2 className="lg:mb-6 mb-0 text-lg font-normal">
                            {t("loginSubTitle")}
                        </h2>
                    </div>
                    <form
                        className="flex flex-col m-auto gap-4 pt-8"
                        onSubmit={async (e) => {
                            e.preventDefault();
                            try {
                                const res = await mutation.mutateAsync(email);
                                if (res.status === 200) {
                                    setIsMessageSent(true);
                                    setError(false);
                                } else {
                                    setError(true);
                                    emailInputRef.current?.focus?.();
                                }
                            } catch (err) {
                                setError(true);
                                emailInputRef.current?.focus?.();
                            }
                        }}
                    >
                        <label
                            className="text-start font-semibold"
                            htmlFor="email"
                        >
                            {t("loginPlaceHolder")}
                        </label>
                        <input
                            id="email"
                            name="email"
                            autoFocus
                            ref={emailInputRef}
                            autoComplete={t("loginPlaceHolder")}
                            placeholder={t("loginPlaceHolder")}
                            className={`w-full bg-transparent inline-block text-center font-medium px-8 py-3 md:px-10 border-[1px] rounded-full placeholder:text-off-white-100 placeholder:font-thin active:border-black focus:border-black ${error && "bg-[#fff8eb] outline-1 outline-[#c3092b] border-[#c3092b] border-1"}`}
                            type="email"
                            value={email}
                            required
                            onChange={(e) => setEmail(e.target.value)}
                        />
                        {mutation.isError ||
                            (error && (
                                <div className="text-[#c3092b]">
                                    {t("errorMessage")}
                                </div>
                            ))}
                        <button
                            disabled={mutation.isPending}
                            type="submit"
                            className="w-full inline-block text-center font-medium focus px-8 py-3 mt-2 lg:mt-8 rounded-full focus:rounded-full focus-within:rounded-full focus-visible:rounded-full transition select-none text-white bg-gradient-ba-essential hover:bg-gradient-ba disabled:opacity-50 disabled:pointer-events-none"
                        >
                            {t("sendButton")}
                        </button>
                        <div className="text-center font-thin">
                            {t("loginInputDescription")}
                        </div>
                    </form>
                </div>
            ) : (
                <div>
                    <div className="font-thin leading-loose mb-6">
                        <h1>{t("messageSuccess.title")}</h1>
                        <div className="inline-block m-auto text-left">
                            <h2 className="text-xl mt-6 mb-4">
                                {t("messageSuccess.subTitle")}
                                <span className="font-extrabold">{email}</span>
                            </h2>
                            <p className="text-base">
                                {t("messageSuccess.description1")}
                            </p>
                            <p className="text-base">
                                {t("messageSuccess.description2")}
                            </p>
                            <div className="text-base">
                                {t("messageSuccess.tip")}
                            </div>
                        </div>
                    </div>
                    <button
                        className="w-full inline-block text-center font-medium border border-white focus rounded-full focus:rounded-full focus-within:rounded-full focus-visible:rounded-full transition select-none text-white bg-gradient-ba-essential hover:bg-gradient-ba hover:border px-10 py-2"
                        onClick={() => setIsMessageSent(false)}
                    >
                        {t("returnButton")}
                    </button>
                </div>
            )}
        </div>
    );
};
